/**
 * Loads external articles once per page view: the manual list (/assets/insights.json) and the latest
 * Medium articles (through rss2json). A failing source is logged and skipped; the page then shows
 * whatever the other source returned, or the empty state.
 */
import { SITE_CONFIG } from '../../config/site-config';
import { logger } from '../../lib/logger';
import type { ExternalPost } from './insight-types';
import { mediumHandle, mergePosts, parseInsightsJson, parseMediumFeed } from './insights-parsers';

const RSS_TO_JSON_ENDPOINT = 'https://api.rss2json.com/v1/api.json';

let externalPosts: readonly ExternalPost[] = [];
let loaded = false;
let pending: Promise<void> | null = null;

export const getExternalPosts = (): readonly ExternalPost[] => externalPosts;

export const hasLoadedInsights = (): boolean => loaded;

/** Whether any external source is configured, i.e. whether a "Loading…" state makes sense. */
export const hasInsightSources = (): boolean =>
  SITE_CONFIG.MEDIUM_FEED || Boolean(SITE_CONFIG.INSIGHTS_JSON_URL) || SITE_CONFIG.ROUTING === 'path';

async function fetchJson(url: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(url, init);
  if (!response.ok) throw new Error(`HTTP ${response.status} from ${new URL(url, location.href).host}`);
  return response.json();
}

async function loadSource(name: string, load: () => Promise<ExternalPost[]>): Promise<ExternalPost[]> {
  try {
    return await load();
  } catch (error) {
    logger.warn(`Could not load ${name} articles.`, error);
    return [];
  }
}

function sources(): Promise<ExternalPost[]>[] {
  const jobs: Promise<ExternalPost[]>[] = [];
  const jsonUrl = SITE_CONFIG.INSIGHTS_JSON_URL || (SITE_CONFIG.ROUTING === 'path' ? '/assets/insights.json' : '');
  if (jsonUrl) {
    jobs.push(
      loadSource('insights.json', async () => parseInsightsJson(await fetchJson(jsonUrl, { cache: 'no-cache' }))),
    );
  }
  const handle = mediumHandle(SITE_CONFIG.MEDIUM_URL);
  if (SITE_CONFIG.MEDIUM_FEED && handle) {
    const feedUrl = `${RSS_TO_JSON_ENDPOINT}?rss_url=${encodeURIComponent(`https://medium.com/feed/${handle}`)}`;
    jobs.push(loadSource('Medium', async () => parseMediumFeed(await fetchJson(feedUrl))));
  }
  return jobs;
}

export function loadInsights(): Promise<void> {
  if (loaded) return Promise.resolve();
  pending ??= Promise.all(sources()).then((lists) => {
    externalPosts = mergePosts(lists);
    loaded = true;
  });
  return pending;
}
